import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, MessageSquare, Send, Sparkles, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const TONES = ["Professional", "Friendly", "Bold", "Luxury", "Playful", "Inspirational"];

export default function Campaigns() {
  const [type, setType] = useState("email");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("Friendly");
  const [recipients, setRecipients] = useState(500);
  const [items, setItems] = useState([]);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/campaigns");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!topic.trim()) {
      toast.error("Enter a campaign topic");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/campaigns", {
        type,
        topic,
        tone,
        recipients_count: parseInt(recipients) || 100,
      });
      setItems([data, ...items]);
      setTopic("");
      toast.success("Campaign drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate");
    } finally {
      setGenerating(false);
    }
  };

  const send = async (id) => {
    try {
      const { data } = await api.post(`/campaigns/${id}/send`);
      toast.success(`Sent (MOCKED) — ${data.metrics.opens} opens simulated`);
      await load();
    } catch {
      toast.error("Send failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/campaigns/${id}`);
    setItems(items.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  return (
    <div className="space-y-8" data-testid="campaigns-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Campaigns</div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Email & WhatsApp</h1>
        <p className="text-white/50 mt-2 max-w-2xl text-sm">
          Generate, queue and (in MVP) simulate-send broadcast campaigns to your customers.
        </p>
      </header>

      <div className="glass rounded-2xl p-4 border border-amber-500/30 bg-amber-500/[0.04]">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-sm text-white/80">
            <span className="font-semibold text-amber-400">MOCKED delivery.</span> Email send requires Resend/SendGrid keys
            and WhatsApp requires Meta Business / Twilio. The UI generates real AI copy and simulates send metrics so you
            can validate the workflow end-to-end.
          </div>
        </div>
      </div>

      <div className="glass rounded-2xl p-6 lg:p-8">
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setType("email")}
            data-testid="campaign-type-email"
            className={`px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${
              type === "email" ? "brand-gradient" : "glass hover:bg-white/5"
            }`}
          >
            <Mail className="h-4 w-4" /> Email
          </button>
          <button
            onClick={() => setType("whatsapp")}
            data-testid="campaign-type-whatsapp"
            className={`px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2 ${
              type === "whatsapp" ? "brand-gradient" : "glass hover:bg-white/5"
            }`}
          >
            <MessageSquare className="h-4 w-4" /> WhatsApp
          </button>
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <label className="text-xs uppercase tracking-[0.2em] text-white/40">Campaign topic</label>
            <textarea
              rows={2}
              value={topic}
              data-testid="campaign-topic-input"
              onChange={(e) => setTopic(e.target.value)}
              className="mt-2 w-full bg-white/5 border border-white/10 focus:border-orange-500/60 outline-none rounded-lg px-4 py-3 text-sm"
              placeholder="e.g. Summer sale 30% off, new product launch, customer reactivation…"
            />
          </div>
          <div className="grid grid-rows-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Tone</label>
              <select
                value={tone}
                data-testid="campaign-tone-select"
                onChange={(e) => setTone(e.target.value)}
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-2.5 text-sm"
              >
                {TONES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Recipients (simulated)</label>
              <input
                type="number"
                min={1}
                value={recipients}
                data-testid="campaign-recipients-input"
                onChange={(e) => setRecipients(e.target.value)}
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-2.5 text-sm"
              />
            </div>
          </div>
        </div>
        <button
          onClick={create}
          data-testid="campaign-generate-button"
          disabled={generating}
          className="mt-5 inline-flex items-center gap-2 brand-gradient px-6 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange disabled:opacity-50"
        >
          <Sparkles className="h-4 w-4" /> {generating ? "Generating…" : "Generate campaign"}
        </button>
      </div>

      <section>
        <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-4">Drafts & sent</div>
        {items.length === 0 ? (
          <div className="glass rounded-2xl p-10 text-center text-white/50 text-sm">No campaigns yet.</div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {items.map((c) => (
              <div key={c.id} className="glass rounded-2xl p-6" data-testid={`campaign-item-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {c.type === "email" ? <Mail className="h-4 w-4 text-orange-400" /> : <MessageSquare className="h-4 w-4 text-orange-400" />}
                    <span className="text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full glass">{c.type}</span>
                    <span
                      className={`text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full ${
                        c.status === "sent" ? "brand-gradient" : "glass"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>
                  <button onClick={() => remove(c.id)} className="p-1.5 rounded-lg hover:bg-white/5" data-testid={`campaign-delete-${c.id}`}>
                    <Trash2 className="h-4 w-4 text-white/50" />
                  </button>
                </div>

                {c.type === "email" ? (
                  <>
                    <div className="text-xs text-white/40 mb-1">Subject</div>
                    <div className="font-heading font-bold text-lg">{c.content?.subject}</div>
                    <div className="text-xs text-white/40 mt-2">Preheader</div>
                    <div className="text-sm text-white/60">{c.content?.preheader}</div>
                    <div className="text-sm text-white/80 mt-3 leading-relaxed">{c.content?.greeting}</div>
                    {(c.content?.body_paragraphs || []).map((p, i) => (
                      <p key={i} className="text-sm text-white/70 mt-2 leading-relaxed">{p}</p>
                    ))}
                    <div className="mt-3 inline-block brand-gradient px-4 py-2 rounded-lg text-xs font-semibold">
                      {c.content?.cta_text}
                    </div>
                    {c.content?.ps && (
                      <div className="text-xs text-white/50 italic mt-3">PS: {c.content.ps}</div>
                    )}
                  </>
                ) : (
                  <>
                    <pre className="text-sm text-white/80 whitespace-pre-wrap font-sans leading-relaxed">{c.content?.message}</pre>
                    <div className="mt-3 inline-block brand-gradient px-4 py-2 rounded-lg text-xs font-semibold">
                      {c.content?.cta_link_text}
                    </div>
                    {c.content?.follow_up && (
                      <div className="text-xs text-white/50 italic mt-3">Follow-up: {c.content.follow_up}</div>
                    )}
                  </>
                )}

                {c.metrics ? (
                  <div className="mt-5 grid grid-cols-4 gap-2 text-center">
                    {[
                      { l: "Sent", v: c.metrics.sent },
                      { l: "Delivered", v: c.metrics.delivered },
                      { l: "Opens", v: c.metrics.opens },
                      { l: "Clicks", v: c.metrics.clicks },
                    ].map((m) => (
                      <div key={m.l} className="glass rounded-lg p-2">
                        <div className="text-xs text-white/40 uppercase tracking-[0.2em]">{m.l}</div>
                        <div className="font-heading font-bold">{m.v}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={() => send(c.id)}
                    data-testid={`campaign-send-${c.id}`}
                    className="mt-5 brand-gradient px-5 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90"
                  >
                    <Send className="h-4 w-4" /> Send (MOCKED)
                  </button>
                )}

                <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-white/30 font-mono">
                  {c.recipients_count} recipients · {new Date(c.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
